import { useAuth, useUser } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import {
  getMyStatusApiUsersMeGetOptions,
  getMyStatusApiUsersMeGetQueryKey,
  markWelcomeVideoSeenApiUsersMeWelcomeVideoSeenPostMutation,
} from "../generated/api/@tanstack/react-query.gen";

/**
 * Decides whether to show the one-time onboarding welcome video.
 *
 * Shares `AuthProvider`'s `/api/users/me` query key, so this adds no request.
 *
 * The modal is marked seen the moment it is displayed rather than on dismissal,
 * so refreshing mid-video doesn't bring it back. That write invalidates
 * `/api/users/me`, which flips `welcome_video_seen` to true — so visibility is
 * latched off the first resolved response instead of being derived from the
 * query, otherwise the modal would unmount out from under the viewer.
 *
 * If the write fails the user sees the video again next login, which is the
 * right failure direction.
 */
export function useWelcomeVideo() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    ...getMyStatusApiUsersMeGetOptions(),
    enabled: Boolean(isSignedIn && user),
  });

  const markSeen = useMutation({
    ...markWelcomeVideoSeenApiUsersMeWelcomeVideoSeenPostMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: getMyStatusApiUsersMeGetQueryKey(),
      }),
  });

  const [isOpen, setIsOpen] = useState(false);
  const hasDecided = useRef(false);

  const decide = useEffectEvent((seen: boolean) => {
    if (seen) return;
    setIsOpen(true);
    markSeen.mutate({});
  });

  useEffect(() => {
    if (!data || hasDecided.current) return;
    hasDecided.current = true;
    decide(data.welcome_video_seen);
  }, [data]);

  return {
    isOpen,
    videoId: data?.welcome_video_id ?? "",
    dismiss: () => setIsOpen(false),
  };
}
